import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import LoginPage from "./page/login/LoginPage";
import Dashboard from "./page/dashboard/Dashboard";
import DatabaseKeyPage from "./page/login/DatabaseKeyPage";
import BoEData from "./page/BoEData";
import SummaryRev from "./page/SummaryRev";
import SalesByMCNo from "./page/SalesByMCNo";
import ScrapByBOE from "./page/ScrapByBOE";
import WIPByBOE from "./page/WIPByBOE";
import MOOWRPurchase from "./page/MOOWRPurchase";
import BoeDetailPage from "./page/BoeDetailPage";
import ScrapEntry from "./page/ScrapEntry";
import DTAReceipts from "./page/DTAReceipts";
import "./App.css";

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem("isLoggedIn") === "true"
  );

  // ✅ Check login status whenever location changes
  useEffect(() => {
    const storedLogin = localStorage.getItem("isLoggedIn") === "true";
    setIsLoggedIn(storedLogin);
    
    // ✅ Redirect to summary-rev if logged in and at root
    if (storedLogin && location.pathname === "/") {
      navigate("/summary-rev", { replace: true });
    }
  }, [location, navigate]);

  // ✅ Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const menuItems = [
    { name: "Summary Rev", path: "/summary-rev" },
    { name: "Scrap Entry", path: "/scrap-entry" },
    { name: "DTA Receipts", path: "/dta-receipts" },
    // { name: "Sales by BOE No", path: "/sales-by-mcno" },
    // { name: "Scrap BY BOE", path: "/scrap-by-boe" },
    // { name: "WIP By BOE", path: "/wip-by-boe" },
    // { name: "MOOWR Purchase", path: "/moowr-purchase" },
    { name: "Logout", path: "/DatabaseKeyPage" },
  ];

  const handleMenuClick = () => setMenuOpen((prev) => !prev);

  const handleNavigate = (path, name) => {
    setMenuOpen(false);

    // ✅ Handle Logout logic
    if (name === "Logout") {
      sessionStorage.clear(); // ✅ Clear all session data
      localStorage.removeItem("isLoggedIn"); // ✅ Clear login state
      setIsLoggedIn(false);
      console.log("Session and login cleared!");
      navigate("/DatabaseKeyPage");
      return;
    }

    // ✅ Navigate normally
    navigate(path);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* ✅ Menu icon only when logged in and not on login or database key page */}
      {isLoggedIn &&
        location.pathname !== "/login" &&
        location.pathname !== "/" &&
        location.pathname !== "/boe-detail" && (
          <div className="fixed top-3 left-3 z-50" ref={menuRef}>
            <button
              onClick={handleMenuClick}
              className="bg-white p-2 rounded-lg shadow-lg cursor-pointer hover:bg-gray-50 transition-all duration-200 border border-gray-200 hover:shadow-xl"
              aria-label="Menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-gray-700"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
            </button>

            {menuOpen && (
              <div className="absolute top-12 left-0 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[160px] overflow-hidden">
                {menuItems.map((item, index) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item.path, item.name)}
                    className={`w-full px-4 py-3 text-left text-sm transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-2 ${
                      item.name === "Logout"
                        ? "bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800 font-semibold"
                        : "hover:bg-blue-50 hover:text-blue-600"
                    }`}
                    style={{
                      animation: `fadeIn 0.2s ease forwards`,
                      animationDelay: `${index * 0.05}s`,
                      opacity: 0,
                    }}
                  >
                    {(item.name === "Summary Rev" || item.name === "DTA Receipts" || item.name === "Sales by BOE No" || item.name === "Scrap BY BOE" || item.name === "WIP By BOE" || item.name === "MOOWR Purchase") && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                    {item.name === "Scrap Entry" && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                    {item.name === "Logout" && (
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    )}
                    {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

      <Routes>
  {/* ✅ Default page */}
  <Route path="/" element={<DatabaseKeyPage />} />

  {/* ✅ Login page */}
  <Route
    path="/login"
    element={
      <LoginPage
        onLoginSuccess={() => {
          localStorage.setItem("isLoggedIn", "true");
          setIsLoggedIn(true);
        }}
      />
    }
  />

  {/* ✅ BOE Detail Page (for new window) */}
  <Route path="/boe-detail" element={<BoeDetailPage />} />

  {/* ✅ Other pages */}
  <Route path="/dashboard" element={<Dashboard />} />
  <Route path="/boe-data" element={<BoEData />} />
  <Route path="/summary-rev" element={<SummaryRev />} />
  <Route path="/sales-by-mcno" element={<SalesByMCNo />} />
  <Route path="/scrap-by-boe" element={<ScrapByBOE />} />
  <Route path="/wip-by-boe" element={<WIPByBOE />} />
  <Route path="/moowr-purchase" element={<MOOWRPurchase />} />
  <Route path="/DatabaseKeyPage" element={<DatabaseKeyPage />} />
  <Route path="/scrap-entry" element={<ScrapEntry />} />
  <Route path="/dta-receipts" element={<DTAReceipts />} />
</Routes>
    </div>
  );
}

export default App;
